import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { PromptCategory } from '@common/model/admin/thirdPartyApi';
import { ClientPhotoPurpose } from '@common/model/client/photo/purpose';
import { coreClientPhotoApi } from 'src/entities/clientPhoto';
import { useResumeData } from 'src/entities/resume';
import { AiImageGeneratorFormData, GenerationStatusItem, ImagePurpose, PromptSelection, PurposeGenerationState } from '../types';
import { AI_IMAGE_GENERATOR_CONSTANTS, convertUrlToBase64 } from '../lib';
import { buildPromptFromSelection } from '../lib/promptBuilder';
import { thirdPartyApiApi } from '../api/thirdPartyApi';

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

interface UseImageGenerationProps {
  clientId: number;
  resumeId?: number;
  originalImageUrl?: string;
  onPhotoSaved?: () => void;
}

const createEmptyPurposeState = (): PurposeGenerationState => ({
  prompts: {},
  results: [],
  statuses: [],
  isGenerating: false,
});

const createId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export const useImageGeneration = ({ clientId, resumeId, originalImageUrl, onPhotoSaved }: UseImageGenerationProps) => {
  const { refetch } = useResumeData({ resumeId });
  const [resumeState, setResumeState] = useState<PurposeGenerationState>(createEmptyPurposeState);
  const [avatarState, setAvatarState] = useState<PurposeGenerationState>(createEmptyPurposeState);
  const [sourceError, setSourceError] = useState<string | null>(null);
  const sourceOriginalUrlRef = useRef<string | undefined>(originalImageUrl);

  const { watch, setValue } = useForm<AiImageGeneratorFormData>({
    defaultValues: {
      selectedImage: null,
      selectedImageUrl: originalImageUrl || '',
      selectedImageBase64: '',
      isConverting: false,
    },
  });

  const formValues = watch();

  const { data: prompts = [] } = thirdPartyApiApi.useGetPromptsQuery();
  const [generateImage] = thirdPartyApiApi.useGenerateImageMutation();
  const [fetchGenerateStatus] = thirdPartyApiApi.useLazyGetGenerateImageStatusQuery();
  const [uploadPhoto, { isLoading: isSaving }] = coreClientPhotoApi.useCreateClientPhotoMutation();
  const defaultsAppliedRef = useRef(false);
  const activePollsRef = useRef(new Set<string>());

  useEffect(() => {
    if (!prompts.length || defaultsAppliedRef.current) {
      return;
    }

    const pickDefaultId = (category: PromptCategory) => prompts.find((p) => p.category === category && p.isDefault)?.id;

    const defaults: PromptSelection = {
      clothing: pickDefaultId(PromptCategory.CLOTHING),
      wall: pickDefaultId(PromptCategory.WALL),
      smile: pickDefaultId(PromptCategory.SMILE),
    };

    if (defaults.clothing === undefined && defaults.wall === undefined && defaults.smile === undefined) {
      return;
    }

    const applyDefaults = (prev: PurposeGenerationState): PurposeGenerationState => {
      const nextPrompts = { ...prev.prompts };
      let changed = false;

      (['clothing', 'wall', 'smile'] as const).forEach((key) => {
        if (nextPrompts[key] === undefined && defaults[key] !== undefined) {
          nextPrompts[key] = defaults[key];
          changed = true;
        }
      });

      return changed ? { ...prev, prompts: nextPrompts } : prev;
    };

    setResumeState(applyDefaults);
    setAvatarState(applyDefaults);
    defaultsAppliedRef.current = true;
  }, [prompts]);

  useEffect(() => {
    if (!originalImageUrl || formValues.selectedImageBase64) {
      return;
    }

    let cancelled = false;

    const initializeBase64 = async () => {
      try {
        setValue('isConverting', true);
        const base64 = await convertUrlToBase64(originalImageUrl);
        if (cancelled) {
          return;
        }
        setValue('selectedImageBase64', base64);
        if (!sourceOriginalUrlRef.current) {
          sourceOriginalUrlRef.current = originalImageUrl;
        }
      } catch (error) {
        if (cancelled) {
          return;
        }
        console.error('Ошибка инициализации изображения:', error);
        setSourceError('Ошибка загрузки исходного изображения');
      } finally {
        if (!cancelled) {
          setValue('isConverting', false);
        }
      }
    };

    initializeBase64();

    return () => {
      cancelled = true;
    };
  }, [originalImageUrl, formValues.selectedImageBase64, setValue]);

  const setSourceImage = useCallback(
    (url: string | undefined) => {
      setValue('selectedImageUrl', url || '');
      if (url?.startsWith('data:')) {
        setValue('selectedImageBase64', url);
        sourceOriginalUrlRef.current = url;
      } else if (!url) {
        setValue('selectedImageBase64', '');
        sourceOriginalUrlRef.current = originalImageUrl;
      } else {
        setValue('selectedImageBase64', '');
        sourceOriginalUrlRef.current = url;
      }
    },
    [originalImageUrl, setValue],
  );

  const getPurposeState = useCallback(
    (purpose: ImagePurpose) => (purpose === 'resume' ? resumeState : avatarState),
    [resumeState, avatarState],
  );

  const setPurposeState = useCallback((purpose: ImagePurpose, updater: (prev: PurposeGenerationState) => PurposeGenerationState) => {
    if (purpose === 'resume') {
      setResumeState(updater);
    } else {
      setAvatarState(updater);
    }
  }, []);

  const handlePromptChange = useCallback(
    (purpose: ImagePurpose, key: keyof PromptSelection, value: number | undefined) => {
      setPurposeState(purpose, (prev) => ({
        ...prev,
        prompts: {
          ...prev.prompts,
          [key]: value,
        },
      }));
    },
    [setPurposeState],
  );

  const handleGenerate = useCallback(
    async (purpose: ImagePurpose, croppedImageBase64?: string) => {
      const imageBase64 = croppedImageBase64 || formValues.selectedImageBase64;

      if (!imageBase64 && !formValues.selectedImageUrl) {
        setSourceError('Необходимо выбрать изображение для генерации');
        return;
      }

      if (!imageBase64) {
        setSourceError('Не удалось подготовить исходное изображение');
        return;
      }

      setSourceError(null);

      const purposeState = getPurposeState(purpose);
      const { prompt, attachmentId } = buildPromptFromSelection(prompts, purposeState.prompts);

      if (!prompt.trim()) {
        setPurposeState(purpose, (prev) => ({
          ...prev,
          statuses: [
            ...prev.statuses,
            {
              id: createId(),
              kind: 'error',
              message: 'Не найден базовый промпт для генерации',
            },
          ],
        }));
        return;
      }

      const pendingId = createId();
      const pendingStatus: GenerationStatusItem = { id: pendingId, kind: 'pending' };
      activePollsRef.current.add(pendingId);

      setPurposeState(purpose, (prev) => ({
        ...prev,
        isGenerating: true,
        statuses: [...prev.statuses, pendingStatus],
      }));

      const markError = (errorMessage: string) => {
        if (!activePollsRef.current.has(pendingId)) {
          return;
        }
        setPurposeState(purpose, (prev) => {
          const statuses = prev.statuses.map((s) =>
            s.id === pendingId
              ? {
                  ...s,
                  kind: 'error' as const,
                  message: errorMessage,
                }
              : s,
          );
          return {
            ...prev,
            isGenerating: statuses.some((s) => s.kind === 'pending'),
            statuses,
          };
        });
      };

      try {
        const { taskId } = await generateImage({
          imageUrl: imageBase64,
          prompt,
          clientId,
          attachmentId,
        }).unwrap();

        if (!taskId) {
          markError('Сервер не вернул taskId');
          return;
        }

        let settled = false;
        let transientFailures = 0;

        for (let attempt = 0; attempt < AI_IMAGE_GENERATOR_CONSTANTS.GENERATE_POLL_MAX_ATTEMPTS; attempt++) {
          if (!activePollsRef.current.has(pendingId)) {
            return;
          }

          if (attempt > 0) {
            await sleep(AI_IMAGE_GENERATOR_CONSTANTS.GENERATE_POLL_INTERVAL_MS);
          }

          if (!activePollsRef.current.has(pendingId)) {
            return;
          }

          let status;
          try {
            status = await fetchGenerateStatus(taskId, false).unwrap();
            transientFailures = 0;
          } catch (statusError) {
            transientFailures += 1;
            console.warn('Transient generate-image status error:', statusError);
            if (transientFailures >= AI_IMAGE_GENERATOR_CONSTANTS.GENERATE_STATUS_TRANSIENT_RETRIES) {
              throw statusError;
            }
            continue;
          }

          if (!status || status.status === 'pending') {
            continue;
          }

          if (status.status === 'fail') {
            markError(status.message || 'Ошибка генерации');
            settled = true;
            break;
          }

          if (status.status !== 'success') {
            transientFailures += 1;
            if (transientFailures >= AI_IMAGE_GENERATOR_CONSTANTS.GENERATE_STATUS_TRANSIENT_RETRIES) {
              markError('Некорректный ответ статуса генерации');
              settled = true;
              break;
            }
            continue;
          }

          const resultImageUrl = status.imageBase64 || status.generatedImageUrl;
          if (!resultImageUrl) {
            markError('Сервер не вернул изображение');
            settled = true;
            break;
          }

          setPurposeState(purpose, (prev) => {
            const statuses = prev.statuses.filter((s) => s.id !== pendingId);
            return {
              ...prev,
              isGenerating: statuses.some((s) => s.kind === 'pending'),
              statuses,
              results: [
                ...prev.results,
                {
                  id: createId(),
                  imageUrl: resultImageUrl,
                },
              ],
            };
          });
          settled = true;
          break;
        }

        if (!settled && activePollsRef.current.has(pendingId)) {
          markError('Превышено время ожидания генерации');
        }
      } catch (error) {
        console.error('Ошибка генерации изображения:', error);

        const err = error as { data?: { message?: string }; message?: string };
        const errorMessage = err?.data?.message || err?.message || 'Ошибка на стороне сервера или ИИ';
        markError(errorMessage);
      } finally {
        activePollsRef.current.delete(pendingId);
      }
    },
    [
      formValues.selectedImageBase64,
      formValues.selectedImageUrl,
      getPurposeState,
      prompts,
      setPurposeState,
      generateImage,
      fetchGenerateStatus,
      clientId,
    ],
  );

  const handleSavePhoto = useCallback(
    async ({
      columnPurpose,
      resultId,
      savePurpose,
      croppedDataUrl,
    }: {
      columnPurpose: ImagePurpose;
      resultId: string;
      savePurpose: ClientPhotoPurpose;
      croppedDataUrl?: string;
    }) => {
      const purposeState = getPurposeState(columnPurpose);
      const resultItem = purposeState.results.find((r) => r.id === resultId);
      const croppedSrc = croppedDataUrl || resultItem?.imageUrl;

      if (!croppedSrc) {
        setSourceError('Нет сгенерированного изображения');
        return false;
      }

      const originalSrc = sourceOriginalUrlRef.current || originalImageUrl;
      if (!originalSrc) {
        setSourceError('Нет исходного изображения');
        return false;
      }

      try {
        const [croppedBlob, originalBlob] = await Promise.all([
          fetch(croppedSrc).then((r) => r.blob()),
          fetch(originalSrc).then((r) => r.blob()),
        ]);

        await uploadPhoto({
          clientId,
          original: originalBlob,
          cropped: croppedBlob,
          purpose: savePurpose,
          skipPatronageUpdate: true,
        }).unwrap();

        setPurposeState(columnPurpose, (prev) => ({
          ...prev,
          results: prev.results.map((r) =>
            r.id === resultId
              ? {
                  ...r,
                  savedPurposes: r.savedPurposes?.includes(savePurpose) ? r.savedPurposes : [...(r.savedPurposes ?? []), savePurpose],
                }
              : r,
          ),
        }));

        if (resumeId) {
          refetch();
        }
        onPhotoSaved?.();
        return true;
      } catch (error) {
        console.error('Ошибка сохранения фото:', error);
        setSourceError('Ошибка сохранения фото');
        return false;
      }
    },
    [getPurposeState, uploadPhoto, clientId, originalImageUrl, resumeId, refetch, onPhotoSaved, setPurposeState],
  );

  const handleResultImageChange = useCallback(
    (purpose: ImagePurpose, resultId: string, url: string | undefined) => {
      setPurposeState(purpose, (prev) => {
        if (!url) {
          return {
            ...prev,
            results: prev.results.filter((r) => r.id !== resultId),
          };
        }

        return {
          ...prev,
          results: prev.results.map((r) => (r.id === resultId ? { ...r, imageUrl: url } : r)),
        };
      });
    },
    [setPurposeState],
  );

  const dismissStatus = useCallback(
    (purpose: ImagePurpose, statusId: string) => {
      activePollsRef.current.delete(statusId);
      setPurposeState(purpose, (prev) => {
        const statuses = prev.statuses.filter((s) => s.id !== statusId);
        return {
          ...prev,
          statuses,
          isGenerating: statuses.some((s) => s.kind === 'pending'),
        };
      });
    },
    [setPurposeState],
  );

  return {
    formValues,
    setValue,
    setSourceImage,
    sourceError,
    isSaving,
    prompts,
    resumeState,
    avatarState,
    handlePromptChange,
    handleGenerate,
    handleSavePhoto,
    handleResultImageChange,
    dismissStatus,
  };
};
